import type { DocLibraryItem } from "./IProjectDocsModel";

export interface ProductDocsQuery {
  readonly customerId: string;
  /**
   * Authored or already-normalized product name. The store runs it through
   * `normalizeProductNameKey` before Dynamo lookups.
   */
  readonly productKey: string;
}

export interface ProductDocsByKeysQuery {
  readonly customerId: string;
  /** Document name keys; the store normalizes each before Dynamo lookups. */
  readonly documentNameKeys: ReadonlyArray<string>;
}

export type ProductReferenceState = "found" | "missing" | "archived" | "unavailable";

export interface ProductReferenceStatus {
  readonly productKey: string;
  readonly state: ProductReferenceState;
  readonly displayName: string;
  readonly reason?: string;
}

/**
 * Store for workflow-product documents.
 *
 * Product lookup is read-backed by `projectstore-metadata`; writes stay behind
 * the Define app until a browser-safe management contract exists.
 */
export interface IProductDocsStoreModel {
  /** True only once this client can accept product-scoped writes. */
  readonly canWrite: boolean;

  /** Documents for the product named by the loaded workflow. */
  getDocsForProductAsync(
    query: ProductDocsQuery,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<DocLibraryItem>>;

  /** Canonical documents named directly by `workflow.knowledge.include[].doc`. */
  getDocsByKeysAsync(
    query: ProductDocsByKeysQuery,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<DocLibraryItem>>;

  /** Product row status used by workflow validation. */
  getProductStatusAsync(
    query: ProductDocsQuery,
    signal?: AbortSignal,
  ): Promise<ProductReferenceStatus>;

  /** Cached `blob:` URL of a document thumbnail, when the backing store has one. */
  getThumbnailUrlAsync(
    query: ProductDocsQuery,
    fileName: string,
    signal?: AbortSignal,
  ): Promise<string | null>;

  /** Open/download a document from the product store. */
  openAsync(
    query: ProductDocsQuery,
    storagePath: string,
    signal?: AbortSignal,
  ): Promise<boolean>;
}

export const PRODUCT_DOCS_STORE_MODEL = Symbol.for("Campfire.ProductDocsStoreModel");

export const PRODUCT_DOCS_STORE_UNAVAILABLE =
  "Workflow product document management is not available in this client.";
